// Library methods
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import ConnectionCard from "./ConnectionCard/ConnectionCard";
import PacServerCard from "./PacServerCard/PacServerCard";
import HubSection from "./HubSection/HubSection";
import AddMachineModal from "./AddMachineModal/AddMachineModal";
import AddWorklistModal from "./AddWorklistModal/AddWorklistModal";
import AddReportDestinationModal from "./AddReportDestinationModal/AddReportDestinationModal";
import HubProblems from "./HubProblems/HubProblems";
import HubUpdateChip from "src/pages/Emr/HubUpdateChip/HubUpdateChip";
import SnackbarMessage from "src/components/shared/alerts/SnackbarMessage/SnackbarMessage";
import {
  CalendarIcon,
  ChevronLeftIcon,
  FolderIcon,
  MachineIcon,
  ReportIcon,
  ServerIcon,
} from "src/assets/icons";

// Hooks
import { useEMRHubById } from "src/services/emr/hooks/emr-hubs/useEMRHubById";
import { useEMRHubConnections } from "src/services/emr/hooks/emr-connections/useEMRHubConnections";
import { useEMRDeviceCatalog } from "src/services/emr/hooks/emr-catalog/useEMRDeviceCatalog";
import { useEMRPacServers } from "src/services/emr/hooks/emr-pac-servers/useEMRPacServers";
import useEMRLiveUpdates from "src/services/emr/live/useEMRLiveUpdates";

// Utils
import {
  groupHubConnectionsByJob,
  hubHasEnabledWorklist,
  nextFreeListenerPort,
} from "src/utils/emr/hubConnectionsHelper";

// Styles
import {
  getSurfaceTokens,
  ModalCustomSurfaceLink,
  PageContainerWrapper,
  getEmrPageBackground,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

type OpenModal = "Worklist" | "Machine" | "Reports" | null;

const PacIcon = () => <ServerIcon width="20" height="20" aria-hidden="true" />;

const EmrConnections = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { hubId } = useParams();
  const { clinicInfo, isLoading } = useClinicContext();
  const { snackbarInfo, setSnackbarInfo } = useSnackbarContext();

  const [openModal, setOpenModal] = useState<OpenModal>(null);

  useEMRLiveUpdates(clinicInfo?.id);

  const { data: emrHub } = useEMRHubById(clinicInfo?.id, hubId);
  const { data: emrConnections, isLoading: isLoadingConnections } =
    useEMRHubConnections(clinicInfo?.id, hubId);
  const { data: catalog, isLoading: isLoadingCatalog } = useEMRDeviceCatalog(
    clinicInfo?.id
  );
  const { data: pacServers, isLoading: isLoadingPacServers } = useEMRPacServers(
    clinicInfo?.id,
    hubId
  );

  const canUseEMR = useMemo(
    () => clinicInfo?.licensing?.canUseEMR && !isLoading,
    [clinicInfo?.licensing?.canUseEMR, isLoading]
  );

  const devices = useMemo(() => catalog?.devices ?? [], [catalog?.devices]);
  const findDevice = (deviceKey: string | null | undefined) =>
    devices.find((device) => device.key === deviceKey);

  const groups = useMemo(
    () => groupHubConnectionsByJob(emrConnections, pacServers),
    [emrConnections, pacServers]
  );

  const folderPaths = useMemo(
    () =>
      emrConnections
        ?.map((connection) => connection?.config?.path)
        .filter((path): path is string => Boolean(path)),
    [emrConnections]
  );

  const listenPort = useMemo(
    () => nextFreeListenerPort(emrConnections, pacServers),
    [emrConnections, pacServers]
  );

  const canAdd = emrHub?.status !== "Revoked";
  const hubIsActive = emrHub?.status === "Active";
  const canAddDevices = canAdd && devices.length > 0;

  const isClinicReady = Boolean(clinicInfo?.id) && !isLoading;

  useEffect(() => {
    if (isClinicReady && !canUseEMR) {
      navigate("/dashboard");
    }
  }, [navigate, canUseEMR, isClinicReady]);

  return (
    <Box
      sx={{
        ...PageContainerWrapper(theme),
        background: getEmrPageBackground(theme),
        color: getSurfaceTokens(theme).onSurface,
        padding: "0px",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          maxHeight: "fit-content",
          gap: "16px",
          padding: "16px",
        }}
      >
        <Box display="flex" width="100%" flexDirection="column" gap="16px">
          <Box display="flex" justifyContent="flex-start">
            <Button
              startIcon={<ChevronLeftIcon />}
              sx={ModalCustomSurfaceLink(theme)}
              onClick={() => navigate("/data-hub")}
            >
              {t("Back to Data Hub")}
            </Button>
          </Box>
          <Box display="flex" alignItems="center" gap="8px" minWidth={0}>
            <Typography
              component="h1"
              fontSize="16px"
              fontWeight={600}
              textOverflow="ellipsis"
              overflow="hidden"
              whiteSpace="nowrap"
              minWidth={0}
            >
              {emrHub?.alias}
            </Typography>
            {emrHub && <HubUpdateChip emrHub={emrHub} />}
          </Box>

          <HubProblems clinicId={clinicInfo?.id} hubId={hubId} />

          <HubSection
            id="emr-worklist"
            icon={<CalendarIcon />}
            title={t("Worklist")}
            description={t(
              "Who is booked, so exams land on the right patient. From a PAC server, which can then be queried for those patients' exams, or from a CSV your EMR drops in a folder."
            )}
            addLabel={t("Add worklist")}
            canAdd={canAddDevices}
            onAdd={() => setOpenModal("Worklist")}
            isLoading={isLoadingPacServers || isLoadingConnections}
            isEmpty={
              !groups.worklistPacServers.length && !groups.worklists.length
            }
            emptyText={t("No worklist yet.")}
          >
            {groups.worklistPacServers.map((pacServer) => (
              <PacServerCard
                hubIsActive={hubIsActive}
                key={pacServer.pacServerId}
                clinicId={clinicInfo?.id}
                hubId={hubId}
                pacServer={pacServer}
                device={findDevice(pacServer.deviceKey)}
                icon={<PacIcon />}
                listenPort={listenPort}
                hubHasOtherWorklist={hubHasEnabledWorklist(
                  emrConnections,
                  pacServer.pacServerId
                )}
                canEdit={canAdd}
                jobKeys={["worklist", "exams"]}
              />
            ))}
            {groups.worklists.map((connection) => (
              <ConnectionCard
                hubIsActive={hubIsActive}
                key={connection.connectionId}
                connection={connection}
                device={findDevice(connection.deviceKey)}
                icon={
                  connection.config?.mode === "CsvFolder" ? (
                    <FolderIcon />
                  ) : (
                    <PacIcon />
                  )
                }
                sharedFolderPaths={folderPaths}
              />
            ))}
          </HubSection>

          <HubSection
            id="emr-machines"
            icon={<MachineIcon />}
            title={t("Machine")}
            description={t(
              "A machine sending its exams straight to the hub, over DICOM or by exporting files to a folder."
            )}
            addLabel={t("Add machine")}
            canAdd={canAddDevices}
            onAdd={() => setOpenModal("Machine")}
            isLoading={isLoadingConnections}
            isEmpty={!groups.machines.length}
            emptyText={t("No machines yet.")}
          >
            {groups.machines.map((connection) => (
              <ConnectionCard
                hubIsActive={hubIsActive}
                key={connection.connectionId}
                connection={connection}
                device={findDevice(connection.deviceKey)}
                icon={
                  connection.sourceKind === "SharedFolder" ? (
                    <FolderIcon />
                  ) : (
                    <MachineIcon />
                  )
                }
                sharedFolderPaths={folderPaths}
                renderEditModal={
                  connection.deviceKey
                    ? (onClose) => (
                        <AddMachineModal
                          open
                          clinicId={clinicInfo?.id}
                          hubId={hubId}
                          hub={emrHub}
                          devices={devices}
                          listenPort={listenPort}
                          sharedFolderPaths={folderPaths}
                          connection={connection}
                          onClose={onClose}
                        />
                      )
                    : undefined
                }
              />
            ))}
          </HubSection>

          <HubSection
            id="emr-retinalogik-exams"
            icon={<ReportIcon />}
            title={t("RetinaLogik Exams")}
            description={t(
              "Where finished RetinaLogik reports go back to the clinic: a PAC server, or a folder your EMR imports from."
            )}
            addLabel={t("Add destination")}
            canAdd={canAddDevices}
            onAdd={() => setOpenModal("Reports")}
            isLoading={isLoadingPacServers || isLoadingConnections}
            isEmpty={
              !groups.reportPacServers.length &&
              !groups.reportDestinations.length
            }
            emptyText={t("RetinaLogik reports are not sent anywhere yet.")}
          >
            {groups.reportPacServers.map((pacServer) => (
              <PacServerCard
                hubIsActive={hubIsActive}
                key={pacServer.pacServerId}
                clinicId={clinicInfo?.id}
                hubId={hubId}
                pacServer={pacServer}
                device={findDevice(pacServer.deviceKey)}
                icon={<PacIcon />}
                listenPort={listenPort}
                hubHasOtherWorklist={hubHasEnabledWorklist(
                  emrConnections,
                  pacServer.pacServerId
                )}
                canEdit={canAdd}
                jobKeys={["reports"]}
              />
            ))}
            {groups.reportDestinations.map((connection) => (
              <ConnectionCard
                hubIsActive={hubIsActive}
                key={connection.connectionId}
                connection={connection}
                device={findDevice(connection.deviceKey)}
                icon={
                  connection.sourceKind === "ExportFolder" ? (
                    <FolderIcon />
                  ) : (
                    <PacIcon />
                  )
                }
                sharedFolderPaths={folderPaths}
              />
            ))}
          </HubSection>

          {isLoadingCatalog && (
            <Typography fontSize="12px">
              {t("Loading the device list...")}
            </Typography>
          )}
        </Box>
      </Box>

      {openModal === "Worklist" && (
        <AddWorklistModal
          open
          clinicId={clinicInfo?.id}
          hubId={hubId}
          hub={emrHub}
          devices={devices}
          pacServers={pacServers}
          listenPort={listenPort}
          sharedFolderPaths={folderPaths}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "Machine" && (
        <AddMachineModal
          open
          clinicId={clinicInfo?.id}
          hubId={hubId}
          hub={emrHub}
          devices={devices}
          listenPort={listenPort}
          sharedFolderPaths={folderPaths}
          onClose={() => setOpenModal(null)}
        />
      )}
      {openModal === "Reports" && (
        <AddReportDestinationModal
          open
          clinicId={clinicInfo?.id}
          hubId={hubId}
          hub={emrHub}
          devices={devices}
          pacServers={pacServers}
          listenPort={listenPort}
          folderPaths={folderPaths}
          onClose={() => setOpenModal(null)}
        />
      )}

      <SnackbarMessage
        snackbarInfo={snackbarInfo}
        setSnackbarInfo={setSnackbarInfo}
      />
    </Box>
  );
};

export default EmrConnections;
